/**
 * @module useConfirmDialog
 * Imperative confirm dialog with Promise-based resolution.
 *
 * Usage:
 *   const { confirm } = useConfirmDialog();
 *   const ok = await confirm({ title: "Delete?", message: "This cannot be undone." });
 *   if (ok) { ... }
 *
 * The dialog state lives at module scope so there is only ever one active
 * confirmation dialog. Calling `confirm()` while a dialog is already open
 * would overwrite the previous promise — callers must await before calling again.
 *
 * Optional `requireText` mode forces the user to type a specific string before
 * the confirm button becomes active, used for destructive operations like
 * deleting all records.
 *
 * The `ConfirmDialog.vue` component reads state from this composable and calls
 * `handleConfirm` / `handleCancel` to resolve the promise.
 */

import { ref } from "vue";

// ── Singleton state ───────────────────────────────────────────────────────────

const showConfirmDialog = ref(false);
const confirmDialogProps = ref({});
let resolvePromise = null; // Captures the Promise resolve function so handlers can call it

const confirm = (options = {}) => {
  return new Promise((resolve) => {
    showConfirmDialog.value = true;
    confirmDialogProps.value = {
      title: options.title || "Confirmation Required",
      message: options.message || "",
      htmlMessage: options.htmlMessage || "",      // Rich HTML content (sanitised by the component)
      type: options.type || "warning",             // "warning" | "error" | "info"
      cancelText: options.cancelText || "Cancel",
      confirmText: options.confirmText || "Confirm",
      requireText: options.requireText || false,   // If true, user must type `requiredText` to confirm
      requiredText: options.requiredText || "CONFIRM",
      textLabel: options.textLabel || "Type to confirm",
      textPlaceholder: options.textPlaceholder || "",
      warningText: options.warningText || "",
      maxWidth: options.maxWidth || 500,
      thirdText: options.thirdText || "",        // Optional third action button label
      thirdColor: options.thirdColor || "primary", // Third button color
    };
    resolvePromise = resolve;
  });
};

/** Resolve with "third" — used for the optional third action button */
const handleThird = () => {
  showConfirmDialog.value = false;
  if (resolvePromise) {
    resolvePromise("third");
    resolvePromise = null;
  }
};

const handleConfirm = () => {
  showConfirmDialog.value = false;
  if (resolvePromise) {
    resolvePromise(true);
    resolvePromise = null;
  }
};

const handleCancel = () => {
  showConfirmDialog.value = false;
  if (resolvePromise) {
    resolvePromise(false);
    resolvePromise = null;
  }
};

export function useConfirmDialog() {
  return {
    showConfirmDialog,
    confirmDialogProps,
    confirm,
    handleConfirm,
    handleCancel,
    handleThird,
  };
}
